import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';
import { SsoProtocol } from '@tinct/core-api-models';
import { TriangleAlertIcon } from 'lucide-react';

export type SwitchSsoProtocolModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The protocol being switched to. */
  protocol: SsoProtocol;
  onConfirm: () => void;
};

const PROTOCOL_LABELS: Record<SsoProtocol, string> = {
  oidc: 'OpenID Connect',
  saml: 'SAML 2.0',
};

/**
 * Asked before an existing connection changes protocol, because the API treats it as a change of
 * trust material and it costs more than a form reset: the connection returns to draft, so single
 * sign-on stops and the requirement is lifted; the settings of the protocol being left go with it
 * (the client secret for OIDC, the registered certificates for SAML); and everyone bound to the
 * connection binds again at their next sign-in, another entity ID being another directory.
 */
export const SwitchSsoProtocolModal = ({
  open,
  onOpenChange,
  protocol,
  onConfirm,
}: SwitchSsoProtocolModalProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>
          Switch this connection to {PROTOCOL_LABELS[protocol]}?
        </DialogTitle>
        <DialogDescription>
          You will describe your identity provider again, with the values{' '}
          {PROTOCOL_LABELS[protocol]} asks for.
        </DialogDescription>
      </DialogHeader>

      <Alert variant="important">
        <TriangleAlertIcon />
        <AlertDescription>
          <span className="flex flex-col gap-1">
            <span>
              Saving the new protocol returns the connection to draft: single
              sign-on is switched off and the requirement lifted until a new
              test sign-in succeeds.
            </span>
            <span>
              {protocol === 'saml'
                ? 'The stored client secret is dropped.'
                : 'The registered signing certificates are removed.'}{' '}
              People who signed in through this connection are matched by their
              address again the next time.
            </span>
          </span>
        </AlertDescription>
      </Alert>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button type="button" onClick={onConfirm}>
          Switch to {PROTOCOL_LABELS[protocol]}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
